import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  AlertTriangle,
  Contrast,
  Layers,
  Loader2,
  Lock,
  LockOpen,
  Monitor,
  Wand2,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useSeriesMetadata } from '../../hooks/useSeriesMetadata';
import { Viewport } from '../../viewer/Viewport';
import { applyToGeometry } from '../../viewer/align/transform';
import type { ViewGeometry } from '../../viewer/align/transform';
import type { EngineCapabilities, ViewportEngine, ViewState } from '../../viewer/ViewportEngine';
import { ViewportControls } from '../viewer-controls/ViewportControls';
import { isOverMinimap } from '../viewer-controls/OverviewMinimap';
import { getDataSource } from '../../data/dataSourceFactory';
import { useAlignment } from './useAlignment';
import { alignSlides, type AutoAlignOutcome } from '../../viewer/align/autoAlign';
import { RotateControl, HorizontalSlider } from './RotateControl';
import { LayoutControl, type CompareLayout } from './LayoutControl';
import { GRID_CAPACITY } from './openSlide';
import { AttachedAction } from './AttachedAction';
import { AnnotationTools, SlideTitle, firstNotInTitle, hasAnnotationTools, type AnnotationToolProps } from './StudyHeader';
import { deriveSeriesInfo } from '../../hooks/useStudyThumbnail';
import { WindowOverlay } from './WindowBox';
import type { WindowLevel } from './useWindowLevel';
import { OBJECTIVE_SCALE, pinchFactor, scaleAt } from '../../viewer/zoomLimits';

export type { CompareLayout };

/** How stacked panes are combined. Difference is the strongest alignment cue —
 *  WSI backgrounds are bright glass, so white minus white is black and a good
 *  overlay goes dark while every misregistered edge lights up. It is unreadable
 *  at partial opacity, so it replaces the fade rather than joining it, and it
 *  applies to the pair the mix currently sits between. */
export type CompareBlend = 'normal' | 'difference';

/** A pane's X-ray window/level, with the engine drawing it. */
export type PaneWindow = { level: WindowLevel; engine: ViewportEngine };

interface Props {
  studyInstanceUID: string;
  /** The series shown, in pane order: A, B and — in the grid — C and, with a
   *  fourth slide, D. */
  paneSeries: (string | undefined)[];
  active: number;
  onActivate: (pane: number) => void;
  locked: boolean;
  onToggleLock: () => void;
  layout: CompareLayout;
  onSetLayout: (layout: CompareLayout) => void;
  /** Panes overlaid rather than side by side. A mode over the arrangement. */
  stacked: boolean;
  onToggleStack: () => void;
  /** Whether the study has enough series for the grid: three or more. */
  canGrid?: boolean;
  onExit: () => void;
  seriesName: (uid: string | undefined) => string;
  /** An image is up in one of the panes: the page marks it seen (features/study/seen.ts). */
  onShown?: (uid: string) => void;
  /** A pane's engine is up, drawing `seriesUID` (or gone, null): the page draws its annotations with it. */
  onPaneEngine?: (pane: number, engine: ViewportEngine | null, seriesUID: string | undefined) => void;
  /**
   * The annotation tools, in the bar beside the compare controls: they draw
   * on whichever pane is pressed, which the right panel then shows.
   */
  tools?: AnnotationToolProps;
  /**
   * A pane's annotations, laid over its slide. Side by side only: the stack
   * blends its panes, which would wash the shapes out with the tissue, and
   * the slide a shape drawn over it lands on would be anyone's guess.
   */
  paneAnnotations?: (pane: number) => ReactNode;
  /**
   * Each pane's X-ray window/level with the engine drawing it, or null for a
   * pane with nothing to window (user, 2026-10-16): its box over the pane, and
   * the drag on it while `windowArmed`. Stacked, one box and one drag, for the
   * driven pane, over the stack as its controls are.
   */
  paneWindows?: (PaneWindow | null)[];
  windowArmed?: boolean;
  /** The X-ray's tools (WindowControl), after the annotation tools behind a divider, as the single view has them. */
  windowControl?: ReactNode;
}

const LABELS = ['A', 'B', 'C', 'D'];

export function CompareView({
  studyInstanceUID,
  paneSeries,
  active,
  onActivate,
  locked,
  onToggleLock,
  layout,
  onSetLayout,
  stacked,
  onToggleStack,
  canGrid,
  onExit,
  seriesName,
  onShown,
  onPaneEngine,
  tools,
  paneAnnotations,
  paneWindows,
  windowArmed = false,
  windowControl,
}: Props) {
  const n = paneSeries.length;
  const stackedRef = useRef(stacked);
  stackedRef.current = stacked;
  const engines = useRef<(ViewportEngine | null)[]>([]);
  const unsubs = useRef<((() => void) | null)[]>([]);
  const applying = useRef(false);
  const lockedRef = useRef(locked);

  // Read the current pair inside stable handlers. Viewport hands the engine back
  // through a ref, so a pane that doesn't remount would otherwise keep whatever
  // closure was live when its engine was created.
  const paneSeriesRef = useRef(paneSeries);
  paneSeriesRef.current = paneSeries;
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  const onPaneEngineRef = useRef(onPaneEngine);
  onPaneEngineRef.current = onPaneEngine;

  // The panes navigate independently until the lock is engaged — overlaying two
  // slides is how you SEE the difference, not a reason to fuse their views. Lock
  // is what freezes the relationship you have lined up.
  const engaged = locked;

  const align = useAlignment({ engines, paneSeriesRef });

  useEffect(() => {
    lockedRef.current = engaged;
    if (!engaged) return;
    align.establish(true);
  }, [engaged, paneSeries, align]);

  // A follower slaved to another slide's view must stop clamping itself to its
  // own extent and zoom ladder, or it snaps back near slide edges and at fit
  // zoom. OpenLayers re-applies those constraints on every resize, so this has
  // to be a mode rather than a one-shot bypass.
  // Also free while merely stacked: lining two slides up means pushing one of
  // them off its own centre, and OpenLayers otherwise pins the centre as soon as
  // the viewport is larger than the slide, so a drag springs straight back.
  useEffect(() => {
    const free = engaged || stacked;
    engines.current.forEach((e) => e?.setFreeView(free));
    return () => engines.current.forEach((e) => e?.setFreeView(false));
  }, [engaged, stacked]);

  const { ref: fsRef, toggle: toggleFullscreen, fullscreen } = useFullscreen<HTMLDivElement>();
  // Stacked hides the pane headers, so the A/B buttons carry the names.
  const paneName = (i: number) => seriesName(paneSeries[i]);

  // ── Automatic alignment ───────────────────────────────────────────────────
  const [aligning, setAligning] = useState(false);
  const [alignment_, setAlignmentOutcome] = useState<AutoAlignOutcome | null>(null);
  // An alignment is applied: the button stays lit and pressing it again puts the
  // slides back where they were.
  const [aligned, setAligned] = useState(false);
  const undoPoses = useRef<(ViewGeometry | null)[]>([]);

  const runAutoAlign = useCallback(async () => {
    const eA = engines.current[0];
    // Built dense rather than sliced: engines register as their panes finish
    // loading, so the array can still be short or holed, and `some` skips holes
    // — a four-up alignment would then run on whichever followers happened to
    // be ready and still light the button as though all of them were done.
    const followers = Array.from(
      { length: paneSeriesRef.current.length - 1 },
      (_, k) => engines.current[k + 1],
    );
    if (!eA || followers.length === 0 || followers.some((e) => !e) || aligning) return;
    setAligning(true);
    setAlignmentOutcome(null);
    undoPoses.current = engines.current.map((e) => e?.getViewGeometry() ?? null);

    // Each follower is registered against pane A independently — A is the
    // reference, so C is not compounded through B and one bad pair cannot drag
    // the rest out. Sequential rather than parallel: each estimate saturates a
    // core for about a second, and running four at once makes the UI stutter for
    // no gain in wall-clock.
    const outcomes: AutoAlignOutcome[] = [];
    for (const eB of followers) {
      outcomes.push(await alignSlides([eA, eB!]));
    }
    setAligning(false);

    // Report the weakest link: the pair that went wrong is the one worth acting
    // on, and one bad pane makes the whole four-up wrong.
    const rank = (o: AutoAlignOutcome) =>
      !o.ok || o.confidence === 'low' ? 0 : o.confidence === 'medium' ? 1 : 2;
    const worst = outcomes.reduce((a, b) => (rank(b) < rank(a) ? b : a));
    setAlignmentOutcome(worst);
    // Low confidence means the estimate is not trustworthy, and a wrong
    // alignment is worse than none — it looks authoritative. Report it and leave
    // the slides exactly where they were.
    if (rank(worst) === 0) return;

    // Move each follower onto the leader through its estimate, then engage the
    // lock. Lock captures the panes' current relative poses, which are now
    // exactly the estimates — so they arrive at the same transforms without this
    // having to reach into the alignment store.
    const gA = eA.getViewGeometry();
    if (!gA) return;
    // Guarded: moving a follower fires its change event, and if the panes are
    // ALREADY locked the follower loop would read that as it leading and drag
    // the others along.
    applying.current = true;
    try {
      followers.forEach((eB, k) => {
        const out = outcomes[k];
        const g = eB!.getViewGeometry();
        if (!out.ok || !out.transform || !g) return;
        eB!.setFreeView(true);
        eB!.setViewGeometry(applyToGeometry(gA, out.transform, g));
      });
    } finally {
      applying.current = false;
    }
    if (!lockedRef.current) onToggleLock();
    setAligned(true);
  }, [aligning, onToggleLock]);

  /** Put every follower back where it was before the estimate, and unlock. */
  const clearAutoAlign = useCallback(() => {
    const poses = undoPoses.current;
    setAligned(false);
    setAlignmentOutcome(null);
    undoPoses.current = [];
    if (lockedRef.current) {
      // Synchronously, not just via onToggleLock: that goes through the URL and
      // a re-render, so the follower loop would still be live when the panes
      // move below and would mirror a restore back onto A — leaving A rotated by
      // the alignment it was never supposed to receive.
      lockedRef.current = false;
      onToggleLock();
    }
    applying.current = true;
    try {
      poses.forEach((pose, i) => {
        if (i > 0 && pose) engines.current[i]?.setViewGeometry(pose);
      });
    } finally {
      applying.current = false;
    }
  }, [onToggleLock]);

  // Unlocking hands the panes back to you. The slides stay exactly where the
  // estimate put them — nothing moves — but they are no longer being held there,
  // so the button stops claiming the alignment is automatic. Watched as a
  // true→false transition rather than "not locked": auto-align sets `aligned`
  // and engages the lock in the same breath, and a plain `!locked` test could
  // catch the render in between and undo itself.
  const wasLocked = useRef(locked);
  useEffect(() => {
    const dropped = wasLocked.current && !locked;
    wasLocked.current = locked;
    if (!dropped) return;
    setAligned(false);
    setAlignmentOutcome(null);
    undoPoses.current = [];
  }, [locked]);

  // What the chip says, if anything: nothing at all when the estimate is solid.
  const alignStatus = !alignment_
    ? null
    : !alignment_.ok
      ? (alignment_.reason ?? 'Unable to align')
      : alignment_.confidence === 'low'
        ? 'Unable to align'
        : alignment_.confidence === 'medium'
          ? 'Check alignment'
          : null;

  // One button: align, then press again to drop the alignment.
  const toggleAutoAlign = useCallback(() => {
    if (aligned) clearAutoAlign();
    else void runAutoAlign();
  }, [aligned, clearAutoAlign, runAutoAlign]);

  const drivenSeries = paneSeries[locked ? 0 : active];
  const getDrivenThumbnail = useCallback(async () => {
    if (!drivenSeries) return null;
    const url = getDataSource().seriesThumbnailUrl(studyInstanceUID, drivenSeries);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        return { url, aspect: img.naturalWidth / img.naturalHeight };
      }
    } catch {
      // No preview built for this series — no minimap.
    }
    return null;
  }, [studyInstanceUID, drivenSeries]);

  // Where the stack is faded to, as a position along A→B→C→D: 0 is pure A, 1 is
  // pure B, and 1.5 is B and C half and half. One number rather than an opacity
  // per pane, because the panes are opaque — only the pane you are fading into
  // and the one beneath it are ever visible, so a single position says
  // everything and the control can be one slider per adjacent pair.
  const [mix, setMix] = useState(0.5);
  const lo = Math.min(Math.floor(mix), n - 2);
  const frac = mix - lo;
  /** Bottom-to-top: everything below the fade is fully painted (and hidden by
   *  what is on top of it), the pane being faded in carries the fraction, and
   *  anything above it is not there yet. */
  const paneOpacity = (i: number) => (i <= lo ? 1 : i === lo + 1 ? frac : 0);
  // Overlay presentation is a viewing preference, not part of the alignment, so
  // it stays local rather than going in the URL alongside compare/a/b/lock.
  // Each pane's live rotation, so the rotation control shows where the slide
  // actually is. Only written when it changes, so panning doesn't re-render.
  const [paneRotation, setPaneRotation] = useState<number[]>([]);
  const paneRotationRef = useRef(paneRotation);
  paneRotationRef.current = paneRotation;
  // The driven pane's live view state, for the controls overlay below. The panes
  // render bare inside the blended stack, so the zoom ladder, scale bar and
  // minimap are rendered once, here, above it.
  const [paneView, setPaneView] = useState<(ViewState | null)[]>([]);
  const driven = locked ? 0 : active;
  const drivenEngine = () => engines.current[driven];
  // Opacity to start with: it shows both slides as themselves, which is what you
  // want before anything is aligned. Grey/red is the check you switch to once
  // there is an alignment to judge.
  const [blend, setBlend] = useState<CompareBlend>('normal');
  const blendRef = useRef(blend);
  blendRef.current = blend;

  useEffect(() => {
    setMix((m) => Math.min(m, n - 1));
  }, [n]);

  // The selected pane is brought into view when the mix is hiding it. Grey/red
  // compares exactly the two panes the mix sits between, and only those two
  // can take the pointer. Selecting one of the others used to leave every
  // pane transparent to the pointer, so the stack simply stopped responding
  // to drags while the toolbar still showed that pane as selected — a frozen
  // viewport with no explanation. Moving the mix to the pane just picked keeps
  // what is selected and what is on screen the same thing. Done from `active`
  // rather than in the press, since a pane is selected from outside too: by
  // the image list (the pane a slide it is asked for is already in), and by
  // the address.
  const activate = onActivate;
  useEffect(() => {
    if (!stackedRef.current || blendRef.current !== 'difference') return;
    setMix((m) => {
      const lo0 = Math.min(Math.floor(m), n - 2);
      return active === lo0 || active === lo0 + 1 ? m : active;
    });
  }, [active, n]);

  // Keep the active pane readable inside stable event handlers. While locked the
  // leader drives and the follower is derived, so keys go to the leader.
  const activeRef = useRef(active);
  activeRef.current = locked ? 0 : active;

  // Keyboard: F = fullscreen (both panes); zoom ladder + / - act on the ACTIVE
  // pane (the panes opt out of their own shortcuts).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) {
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
        return;
      }

      // [ and ] step the driven slide to the next quarter turn, matching the
      // single-slide view. StudyPage gates those keys on !compareMode, so they
      // are free here.
      if (e.key === '[' || e.key === ']') {
        e.preventDefault();
        const engine = engines.current[locked ? 0 : activeRef.current];
        const cur =
          ((Math.round(paneRotationRef.current[locked ? 0 : activeRef.current] ?? 0) % 360) + 360) %
          360;
        engine?.rotateTo(
          e.key === '[' ? (Math.ceil(cur / 90) - 1) * 90 : (Math.floor(cur / 90) + 1) * 90,
        );
        return;
      }

      // `x` flips the blend. The other alignment letters are gone: the panes are
      // navigable independently now, so lining them up is ordinary pan/zoom/rotate
      // on whichever pane is selected rather than a separate nudge mode.
      if (stackedRef.current && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        setBlend((b) => (b === 'difference' ? 'normal' : 'difference'));
        return;
      }

      const engine = engines.current[activeRef.current];
      if (!engine) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        engine.zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        engine.zoomOut();
      } else if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        if (e.repeat) return; // a held digit only re-aims at the same rung
        const d = Number(e.key);
        if (d === 1) engine.fit();
        else {
          const step = d === 0 ? 8 : d - 2;
          if (engine.capabilities.magnification) engine.setMagnification(scaleAt(engine.capabilities.zoomScale ?? OBJECTIVE_SCALE, step / 8));
          else engine.zoomToFraction(step / 8);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggleFullscreen, locked]);

  // Trackpad pinch zooms the pane under the cursor (activating it), or the active
  // pane when over the surrounding UI — never both. Always preventDefault so the
  // page never zooms.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      if (isOverMinimap(e)) return; // it zooms about the spot it points at
      const paneEl = e.target instanceof Element ? e.target.closest('[data-pane]') : null;
      const idx = paneEl ? Number(paneEl.getAttribute('data-pane')) : activeRef.current;
      if (paneEl && idx !== activeRef.current) activate(idx);
      engines.current[idx]?.zoomByFactor(
        pinchFactor(e),
        paneEl ? { x: e.clientX, y: e.clientY } : undefined,
      );
    };
    const onGesture = (e: Event) => e.preventDefault();
    document.addEventListener('wheel', onWheel, { capture: true, passive: false });
    document.addEventListener('gesturestart', onGesture, { passive: false });
    document.addEventListener('gesturechange', onGesture, { passive: false });
    document.addEventListener('gestureend', onGesture, { passive: false });
    return () => {
      document.removeEventListener('wheel', onWheel, { capture: true });
      document.removeEventListener('gesturestart', onGesture);
      document.removeEventListener('gesturechange', onGesture);
      document.removeEventListener('gestureend', onGesture);
    };
  }, [activate]);

  // Push the leader's view through the captured alignment onto the follower.
  // Driven from subscribe() rather than onRender(): subscribe fires on OL's
  // change:center/resolution/rotation, i.e. when the view changes and BEFORE the
  // frame composites, so both maps paint in the same frame. Following on
  // postrender would leave the follower structurally one frame behind, which
  // shows up as visible slip during any animated zoom.
  const handleChange = useCallback(
    (i: number) => {
      // getViewState() builds a fresh object every call, so an identity check
      // would set state on EVERY view event — a re-render of every pane and the
      // controls for each frame of a pan. Compare by value instead.
      const vs = engines.current[i]?.getViewState() ?? null;
      setPaneView((prev) => {
        if (sameViewState(prev[i], vs)) return prev;
        const next = prev.slice();
        next[i] = vs;
        return next;
      });
      const gLeader = engines.current[i]?.getViewGeometry();
      const deg = gLeader?.rotationDeg;
      if (deg != null) {
        setPaneRotation((prev) => {
          if (Math.abs((prev[i] ?? 0) - deg) < 0.01) return prev;
          // Dense: assigning past the end leaves holes, and a hole reaches
          // RotateControl as undefined, which becomes NaN and then a NaN
          // view rotation. Panes that have not reported yet read as 0°.
          const next = Array.from({ length: Math.max(prev.length, i + 1) }, (_, k) => prev[k] ?? 0);
          next[i] = deg;
          return next;
        });
      }
      if (!lockedRef.current || applying.current || !gLeader) return;

      // One leader, every other pane derived from it — never chained through a
      // neighbour, so no pane accumulates another's rounding.
      applying.current = true;
      try {
        for (let j = 0; j < paneSeriesRef.current.length; j++) {
          if (j === i) continue;
          const follower = engines.current[j];
          const t = align.transformFor(paneSeriesRef.current[i], paneSeriesRef.current[j]);
          const gFollower = follower?.getViewGeometry();
          if (!follower || !t || !gFollower) continue;
          follower.setViewGeometry(applyToGeometry(gLeader, t, gFollower));
        }
      } finally {
        applying.current = false;
      }
    },
    [align],
  );

  const setEngine = useCallback(
    (i: number) => (engine: ViewportEngine | null) => {
      unsubs.current[i]?.();
      unsubs.current[i] = null;
      engines.current[i]?.setFreeView(false);
      engines.current[i] = engine;
      if (engine) {
        engine.setFreeView(lockedRef.current || stackedRef.current);
        unsubs.current[i] = engine.subscribe(() => handleChange(i));
        // Every pane's image counts as seen, not only the active one's.
        const uid = paneSeriesRef.current[i];
        if (uid) onShownRef.current?.(uid);
      }
      onPaneEngineRef.current?.(i, engine, engine ? paneSeriesRef.current[i] : undefined);
      // Establish as soon as the engines exist: the engage effect can't, because
      // stack mode engages during mount, before any of them has initialised.
      if (lockedRef.current) align.establish();
    },
    [handleChange, align],
  );

  useEffect(() => {
    return () => {
      unsubs.current.forEach((u) => u?.());
    };
  }, []);

  return (
    <div ref={fsRef} className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Compare toolbar */}
      <div className="relative z-20 flex h-12 shrink-0 items-center gap-0.5 border-b px-3">
        <span className="mr-1 shrink-0 text-xs font-medium text-muted-foreground">Compare</span>
        {/* Tools, centred in the bar the way the single-slide header centres the
            annotation tools: align, lock and stack, then — only when the panes
            are stacked — which slide is driven and how the two are combined. */}
        <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-0.5">
          {/* Anchored to the group's left edge rather than laid out in the bar:
              it sits against the align button it belongs to, and appearing or
              clearing never nudges the tools off centre. A high-confidence
              alignment says nothing — the slides on screen are the report. */}
          {alignStatus && (
            <span
              className="absolute right-full mr-2 flex items-center gap-1 whitespace-nowrap rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-600 dark:text-amber-400"
              title={
                alignment_?.ok
                  ? `overlap ${alignment_.dice?.toFixed(2)} · structure ${alignment_.ncc?.toFixed(2)} · ${alignment_.ms}ms`
                  : undefined
              }
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {alignStatus}
            </span>
          )}
          <ToolButton
            label={
              aligning
                ? 'Estimating alignment…'
                : aligned
                  ? 'Remove the alignment (back to where the slides were)'
                  : 'Align the slides automatically'
            }
            onClick={toggleAutoAlign}
            active={aligning || aligned}
          >
            {aligning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4" />
            )}
          </ToolButton>
          <ToolButton
            label={locked ? 'Unlock navigation' : 'Lock navigation (move together)'}
            onClick={onToggleLock}
            active={locked}
          >
            {locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
          </ToolButton>
          <ToolButton
            label={stacked ? 'Un-stack the slides' : 'Stack the slides on top of each other'}
            onClick={onToggleStack}
            active={stacked}
          >
            <Layers className="h-4 w-4" />
          </ToolButton>
          {stacked && (
            <>
              {/* Which slide the controls drive, and — one continuous track from
                  A through to D — where the stack is faded to. Locked, every
                  letter lights up: the panes move as one, so "which one" stops
                  being a question. */}
              <div className="flex items-center overflow-hidden rounded-md border">
                {paneSeries.map((_, i) => (
                  <PaneButton
                    key={i}
                    pane={i}
                    locked={locked}
                    active={active}
                    name={paneName(i)}
                    onActivate={activate}
                  />
                ))}
                <div className="h-8 w-px shrink-0 bg-border" />
                <HorizontalSlider
                  value={mix / (n - 1)}
                  onChange={(v) => setMix(v * (n - 1))}
                  width={n > 2 ? 140 : 72}
                  stops={n}
                  label="Opacity Mix"
                  detail={
                    frac < 0.005
                      ? `${LABELS[lo]} 100%`
                      : `${LABELS[lo]} ${Math.round((1 - frac) * 100)}% · ${LABELS[lo + 1]} ${Math.round(frac * 100)}%`
                  }
                />
              </div>

              {/* Grey/red is the one presentation the mix slider can't express, so
                  it is a toggle of its own: off is the fade the slider controls. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setBlend(blend === 'difference' ? 'normal' : 'difference')}
                    className={cn(
                      'ml-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                      blend === 'difference'
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                    )}
                  >
                    <Contrast className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  The mixed pair in grey, disagreement in red (x)
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {tools && hasAnnotationTools(tools) && (
            <>
              <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              <AnnotationTools {...tools} />
            </>
          )}
          {windowControl && (
            <>
              <span className="mx-1 h-5 w-px bg-border" aria-hidden />
              {windowControl}
            </>
          )}
        </div>

        {/* Right-hand group, laid out the same way as the single-slide header:
            arrangement, then fine rotation, then fullscreen. */}
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {/* Picking a split re-arranges the panes; picking single leaves compare,
              as does the Exit attached to it (the way out sits on the tool, as
              Done does on the counter). */}
          <AttachedAction action={{ label: 'Exit', hint: 'Exit compare (D)', onClick: onExit }}>
            <LayoutControl
              value={layout}
              offer={(v) => v !== 'grid' || !!canGrid}
              onSelect={(v) => (v === 'single' ? onExit() : onSetLayout(v))}
            />
          </AttachedAction>
          <RotateControl
            deg={paneRotation[locked ? 0 : active] ?? 0}
            onRotate={(deg) => engines.current[locked ? 0 : active]?.rotateTo(deg)}
          />
          <ToolButton
            label={fullscreen ? 'Exit fullscreen' : 'Fullscreen (all panes)'}
            onClick={toggleFullscreen}
          >
            <Monitor className="h-4 w-4" />
          </ToolButton>
        </div>
      </div>

      {/* The element tree is IDENTICAL across all three layouts — only classes and
          styles change. The panes' <Viewport>s are keyed by series UID, so moving
          them in the tree would unmount and remount them, tearing down DMV and
          reloading the whole pyramid every time you toggle overlay. The inner
          wrapper is `display: contents` when not stacked, so it collapses out of
          the flex layout entirely rather than being added and removed. */}
      <div
        className={cn(
          'min-h-0 flex-1',
          // overflow-hidden guards the toolbar: OpenLayers oversizes its canvas
          // when the view is rotated, and an SVG filter paints a region 10%
          // larger than the element it is applied to. Either can reach out of
          // the stack and sit over the controls above it.
          stacked ? 'relative isolate overflow-hidden bg-viewport' : 'flex',
          !stacked && layout === 'horizontal' && 'flex-row',
          !stacked && layout === 'vertical' && 'flex-col',
          // The grid: 2×2, the gap itself drawing the separators.
          !stacked && layout === 'grid' && 'grid grid-cols-2 grid-rows-2 gap-px bg-border',
        )}
      >
        {/* Only the map layers live in here, so the blend applies to the slides
            and nothing else — the controls render above it, unblended. */}
        <div className={cn(stacked ? 'absolute inset-0 isolate' : 'contents')}>
          {paneSeries.map((uid, i) => (
            <Fragment key={i}>
              {/* Divider between adjacent panes in a row or column. The grid
                  draws its own with the gap, and the stack has nothing to
                  divide. */}
              {i > 0 && (
                <div
                  className={cn(
                    'shrink-0 bg-border',
                    stacked || layout === 'grid'
                      ? 'hidden'
                      : layout === 'horizontal'
                        ? 'w-px'
                        : 'h-px',
                  )}
                />
              )}
              <ComparePane
                index={i}
                studyInstanceUID={studyInstanceUID}
                seriesInstanceUID={uid}
                label={LABELS[i]}
                name={seriesName(uid)}
                active={active === i}
                onActivate={() => activate(i)}
                onEngine={setEngine(i)}
                stacked={stacked}
                overlay={stacked}
                annotations={stacked ? null : paneAnnotations?.(i)}
                windowed={paneWindows?.[i] ?? null}
                windowBox={!stacked}
                windowArmed={windowArmed}
                style={
                  stacked
                    ? blend === 'difference'
                      ? // Grey/red compares the two panes the mix currently sits
                        // between: the blend is a per-channel minimum of exactly
                        // two layers, so the rest are held out of the way.
                        i === lo
                        ? {
                            filter: 'url(#cmp-grey)',
                            // The lower of the compared pair is not automatically
                            // the bottom of the stack: with the mix between B and
                            // C it sits above A, so leaving it at the default
                            // `auto` made it swallow gestures meant for whichever
                            // pane was actually selected. Locked it leads, since
                            // the pane below it is not on screen to lead.
                            pointerEvents: locked || active === i ? 'auto' : 'none',
                          }
                        : i === lo + 1
                          ? {
                              // See the filters at the foot of this component.
                              filter: 'url(#cmp-grey-red)',
                              mixBlendMode: 'darken',
                              pointerEvents: !locked && active === i ? 'auto' : 'none',
                            }
                          : { opacity: 0, pointerEvents: 'none' }
                      : {
                          opacity: paneOpacity(i),
                          // Transparent to the pointer unless this is the pane
                          // being driven — otherwise the top of the stack would
                          // swallow every gesture meant for a slide underneath.
                          // Locked, the leader drives and the rest are derived,
                          // so only pane A takes the pointer.
                          pointerEvents: i > 0 && (locked || active !== i) ? 'none' : 'auto',
                        }
                    : undefined
                }
              />
            </Fragment>
          ))}
          {/* The grid's cells past the last pane — one, with three slides.
              Painted like a pane, so each reads as an empty pane rather than a
              hole: the grid's own background is the border colour that draws
              the separators. In the tree in every layout, hidden outside the
              grid, like the dividers: the tree stays identical. */}
          {Array.from({ length: GRID_CAPACITY - n }, (_, k) => (
            <div
              key={`empty-${k}`}
              className={!stacked && layout === 'grid' ? 'bg-viewport' : 'hidden'}
            />
          ))}
        </div>

        {/* Rendered here rather than inside a pane: the panes sit in the blended
            layer, so their own chrome would be screened and channel-filtered
            along with the tissue. One set, driving the selected slide. */}
        {stacked && (
          <ViewportControls
            viewState={paneView[driven] ?? null}
            capabilities={drivenEngine()?.capabilities ?? NO_CAPS}
            isFullscreen={fullscreen}
            onZoomIn={() => drivenEngine()?.zoomIn()}
            onZoomOut={() => drivenEngine()?.zoomOut()}
            onFit={() => drivenEngine()?.fit()}
            onToggleFullscreen={toggleFullscreen}
            onSetMagnification={(m) => drivenEngine()?.setMagnification(m)}
            onPreviewAt={(m) => drivenEngine()?.previewAt(m) ?? Promise.resolve(null)}
            onGetThumbnail={getDrivenThumbnail}
            onNavigate={(x, y, animate) => drivenEngine()?.navigateToFraction(x, y, animate)}
            onZoomAt={(factor, x, y) => drivenEngine()?.zoomAtFraction(factor, x, y)}
            onScrollAt={(e, x, y) => drivenEngine()?.scrollZoomAtFraction(e, x, y)}
            inverted={paneWindows?.[driven]?.level.inverted}
          />
        )}
        {stacked && paneWindows?.[driven] && (
          <WindowOverlay key={driven} level={paneWindows[driven]!.level} engine={paneWindows[driven]!.engine} armed={windowArmed} />
        )}
      </div>

      {/* Both slides render in grey; only disagreement takes on colour.
          Pane A is plain luminance. Pane B is luminance too, but with its red
          channel pinned to full, and the two combine with `darken` — a
          per-channel minimum. Where the slides agree every channel resolves to
          the same luminance and the pixel is grey, so you are simply looking at
          the tissue. Where they disagree, green and blue fall to the darker of
          the two while red stays put, and the pixel goes red in proportion.

          A difference blend is the obvious choice and the wrong one: subtracting
          the slides sends matching tissue to black, so the thing you are trying
          to read vanishes exactly where it lines up. */}
      <svg aria-hidden className="pointer-events-none absolute h-0 w-0">
        <filter id="cmp-grey" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.2126 0.7152 0.0722 0 0
                    0.2126 0.7152 0.0722 0 0
                    0.2126 0.7152 0.0722 0 0
                    0 0 0 1 0"
          />
        </filter>
        <filter id="cmp-grey-red" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1
                    0.2126 0.7152 0.0722 0 0
                    0.2126 0.7152 0.0722 0 0
                    0 0 0 1 0"
          />
        </filter>
      </svg>
    </div>
  );
}

/** Field-wise equality for the controls' view state — see the call site. */
function sameViewState(a: ViewState | null, b: ViewState | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.magnification === b.magnification &&
    a.mmPerPixel === b.mmPerPixel &&
    a.zoomPercent === b.zoomPercent &&
    a.rotationDeg === b.rotationDeg &&
    a.canZoomIn === b.canZoomIn &&
    a.canZoomOut === b.canZoomOut &&
    a.viewRect?.x === b.viewRect?.x &&
    a.viewRect?.y === b.viewRect?.y &&
    a.viewRect?.w === b.viewRect?.w &&
    a.viewRect?.h === b.viewRect?.h
  );
}

const NO_CAPS: EngineCapabilities = {
  magnification: false,
  rotate: false,
  scaleBar: false,
  annotations: false,
};

function ComparePane({
  index,
  studyInstanceUID,
  seriesInstanceUID,
  label,
  name,
  active,
  onActivate,
  onEngine,
  stacked = false,
  overlay = false,
  annotations,
  windowed = null,
  windowBox = true,
  windowArmed = false,
  style,
}: {
  index: number;
  studyInstanceUID: string;
  seriesInstanceUID: string | undefined;
  label: string;
  name: string;
  active: boolean;
  onActivate: () => void;
  onEngine: (e: ViewportEngine | null) => void;
  /** Both panes occupy the same box, one on top of the other. */
  stacked?: boolean;
  /** This is the upper pane of the stack — see Viewport's `overlay`. */
  overlay?: boolean;
  /** Laid over the slide, in the slide's own box. */
  annotations?: ReactNode;
  /** The pane's X-ray window/level (Props paneWindows); its minimap follows Invert. */
  windowed?: PaneWindow | null;
  /** Its window box over the pane: not when stacked, where the stack shows one. */
  windowBox?: boolean;
  windowArmed?: boolean;
  style?: CSSProperties;
}) {
  const { data: metadata } = useSeriesMetadata(studyInstanceUID, seriesInstanceUID);
  // Named exactly as the single-slide header names it: the navigator's
  // "5 of 7 · Name · Stain · Site, Side", with the description (else the
  // anatomic site) beside it when the name does not already say it.
  const info = deriveSeriesInfo(metadata ?? []);
  const title = name || info.slideId || '';
  const subtitle = firstNotInTitle(title, [info.description, info.anatomicSite]);

  return (
    <div
      data-pane={index}
      // A press anywhere in the pane selects it: on the pointer's press, which
      // nothing inside can hold back — the window box and its drag take theirs
      // with preventDefault, which suppresses the mousedown that followed.
      onPointerDownCapture={onActivate}
      className={cn(
        'outline-none',
        stacked ? 'absolute inset-0 flex flex-col' : 'relative flex min-h-0 min-w-0 flex-1 flex-col',
      )}
      style={style}
    >
      {/* Hidden for BOTH panes when stacked — the geometry that keeps the two
          views registered assumes identical viewport boxes, so one pane cannot
          carry a 28px header the other doesn't. Names move to the toolbar. */}
      <div
        className={cn(
          'flex h-7 shrink-0 items-center gap-2 border-b-2 px-2 transition-colors',
          stacked && 'hidden',
          // The image list's blue marks the active pane (user, 2026-09-26): primary is white in dark mode.
          active ? 'border-blue-500 bg-blue-500/10' : 'border-border',
        )}
      >
        <span
          className={cn(
            'rounded px-1.5 text-xs font-semibold',
            active ? 'bg-blue-600 text-white dark:bg-blue-500' : 'bg-muted text-muted-foreground',
          )}
        >
          {label}
        </span>
        <SlideTitle
          label={title}
          uid={seriesInstanceUID}
          className={cn('text-xs', !active && 'text-muted-foreground')}
        />
        {subtitle && (
          <span className="min-w-0 truncate text-xs text-muted-foreground">{subtitle}</span>
        )}
      </div>
      <div className={cn('relative min-h-0 flex-1', !overlay && 'bg-viewport')}>
        {seriesInstanceUID && metadata ? (
          <>
            <Viewport
              key={seriesInstanceUID}
              studyInstanceUID={studyInstanceUID}
              seriesInstanceUID={seriesInstanceUID}
              metadata={metadata}
              onEngineReady={onEngine}
              keyboardShortcuts={false}
              pointerZoom={false}
              overlay={overlay}
              inverted={windowed?.level.inverted ?? false}
            />
            {annotations}
            {windowed && windowBox && <WindowOverlay level={windowed.level} engine={windowed.engine} armed={windowArmed} />}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );
}

/** One end of the A↔B pair: picks which slide the controls drive. */
function PaneButton({
  pane,
  locked,
  active,
  name,
  onActivate,
}: {
  pane: number;
  locked: boolean;
  active: number;
  name: string;
  onActivate: (i: number) => void;
}) {
  const letter = LABELS[pane];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={locked}
          onClick={() => onActivate(pane)}
          className={cn(
            'h-8 w-8 shrink-0 text-xs font-semibold transition-colors',
            locked || active === pane
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {letter}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {locked ? 'Locked — the slides move together' : `Select image ${letter}`}
        <div className="text-[10px] text-muted-foreground">{name}</div>
      </TooltipContent>
    </Tooltip>
  );
}

function ToolButton({
  label,
  onClick,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
            active && 'bg-accent text-primary hover:text-primary',
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}
